import type { LegalDocument, LegalSlug } from '#app/data/legal/types'
import { siteContent } from './content'
import {
	anchorIdFromHref,
	focusLandingTarget,
	isPlainLeftClick,
	targetScrollTop,
} from './glide-scroll'
import { SITE_LINKS } from './site-links'

/**
 * The legal shell's derivations: the switcher entries, a document's table of
 * contents, the scroll-spy rule and the offset its links scroll to. The pure
 * parts at the top are unit tested; the two DOM-driving functions at the
 * bottom are the only place that touches `window`.
 */

/** A heading counts as reached once its top is above this line (px). */
export const SPY_LINE_PX = 160
/** Within this many px of the bottom the last section is the active one. */
export const BOTTOM_SLACK_PX = 4
/** Room between the header and a heading scrolled to from the TOC. */
export const TOC_GAP_PX = 24
/** The hero's id: what "Back to top" points at. */
export const LEGAL_TOP_ID = 'tt-legal-top'

export interface LegalNavEntry {
	slug: LegalSlug
	label: string
	href: string
}

/** The switcher pill under the title, in order. */
export const LEGAL_NAV: readonly LegalNavEntry[] = [
	{
		slug: 'terms',
		label: siteContent.legal.switcher.terms,
		href: SITE_LINKS.terms,
	},
	{
		slug: 'privacy',
		label: siteContent.legal.switcher.privacy,
		href: SITE_LINKS.privacy,
	},
	{
		slug: 'cookies',
		label: siteContent.legal.switcher.cookies,
		href: SITE_LINKS.cookies,
	},
]

export interface TocEntry {
	id: string
	label: string
	href: string
}

/** One table-of-contents link per section, using the short label. */
export function tocEntries(doc: Pick<LegalDocument, 'sections'>): TocEntry[] {
	return doc.sections.map((section) => ({
		id: section.id,
		label: section.tocLabel,
		href: `#${section.id}`,
	}))
}

/**
 * Which section is being read: the last heading whose top has passed the
 * 160px line, or the last section once the page is scrolled to the bottom
 * (so a short final section still lights up). -1 before the first heading.
 * `tops` are document offsets (rect.top + scrollY), so for a given layout
 * the answer depends on the scroll position alone.
 */
export function activeSectionIndex(
	tops: readonly number[],
	scrollY: number,
	viewportHeight: number,
	docHeight: number,
): number {
	if (tops.length === 0) return -1
	if (scrollY + viewportHeight >= docHeight - BOTTOM_SLACK_PX) {
		return tops.length - 1
	}
	let active = -1
	tops.forEach((top, index) => {
		if (top - scrollY < SPY_LINE_PX) active = index
	})
	return active
}

/** How far below the viewport top a TOC target settles: the header plus 24px. */
export function tocScrollOffset(headerHeight: number): number {
	return Math.max(0, headerHeight) + TOC_GAP_PX
}

/* ----------------------------------------------------------------------- */
/* DOM                                                                     */
/* ----------------------------------------------------------------------- */

/** The spy rule applied to the h2s inside `article` at scroll position `y`. */
export function measureActiveSection(article: HTMLElement, y: number): number {
	const headings = article.querySelectorAll<HTMLElement>('h2[id]')
	// One layout pass: every read happens before any write (the state update).
	const tops = Array.from(
		headings,
		(heading) => heading.getBoundingClientRect().top + y,
	)
	return activeSectionIndex(
		tops,
		y,
		window.innerHeight,
		document.documentElement.scrollHeight,
	)
}

export interface LegalLinkOptions {
	/** Selector for the sticky header; its height is part of the offset. */
	headerSelector: string
	/** Client-side navigation for same-site links inside the prose. */
	navigate: (to: string) => void
}

function prefersReducedMotion() {
	return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Update the URL's hash without a jump and without disturbing the router's history state. */
function replaceHash(hash: string) {
	const { pathname, search } = window.location
	window.history.replaceState(
		window.history.state,
		'',
		pathname + search + hash,
	)
}

/**
 * Click delegation for the legal page. Table-of-contents links and "Back to
 * top" scroll smoothly (instantly under reduced motion) so the heading sits
 * 24px under the header, move focus to that heading (or the hero) so the
 * next Tab continues from there, and the URL hash follows so the section
 * can be shared. Same-site links inside the prose (Terms to Privacy) navigate
 * client side. The handler prevents the default first, so the site shell's
 * anchor glide leaves these links alone. Returns a detach function that a
 * React 19 ref callback can return as its cleanup.
 */
export function attachLegalLinks(
	root: HTMLElement,
	options: LegalLinkOptions,
): () => void {
	const onClick = (event: MouseEvent) => {
		if (!isPlainLeftClick(event)) return
		const anchor = (event.target as Element | null)?.closest?.('a[href]')
		if (!(anchor instanceof HTMLAnchorElement) || !root.contains(anchor)) return
		const href = anchor.getAttribute('href') ?? ''
		const id = anchorIdFromHref(href)
		if (id) {
			const target = document.getElementById(id)
			if (!target) return
			event.preventDefault()
			// Focus lands on the target first (without scrolling), so folding
			// the compact "On this page" list never hides the focused link and
			// drops focus to <body>. The list folds before measuring, so the
			// target does not move under the scroll.
			focusLandingTarget(target)
			const details = anchor.closest('details')
			if (details) details.open = false
			const behavior = prefersReducedMotion() ? 'instant' : 'smooth'
			if (id === LEGAL_TOP_ID) {
				window.scrollTo({ top: 0, behavior })
				replaceHash('')
				return
			}
			const header = document.querySelector<HTMLElement>(options.headerSelector)
			const top = targetScrollTop({
				rectTop: target.getBoundingClientRect().top,
				scrollY: window.scrollY,
				offset: tocScrollOffset(header ? header.offsetHeight : 0),
				maxScroll: document.documentElement.scrollHeight - window.innerHeight,
			})
			window.scrollTo({ top, behavior })
			replaceHash(`#${id}`)
			return
		}
		if (
			href.startsWith('/') &&
			!href.startsWith('//') &&
			anchor.closest('[data-tt-legal-prose]')
		) {
			event.preventDefault()
			options.navigate(href)
		}
	}
	root.addEventListener('click', onClick)
	return () => root.removeEventListener('click', onClick)
}
